import { clampParaMilestoneIconTier } from '../components/para-milestone-icon/para-milestone-icon.util';
import { normalizeChipColor } from '../utils/para-color.util';
import type { ParaAssessmentContextDto } from '../services/para-assessment-context.service';

/** Single milestone band from assessment context (`getAssessmentContext`). */
export type ParaMilestoneDefinition = NonNullable<ParaAssessmentContextDto['milestones']>[number];

export type ParaChallengeIndexSection = {
	challengeIndexById: Map<number, number>;
};

/**
 * Phase slice for milestone status: must include `challengeData.challenges` (skip phases with none)
 * and `phaseNumber` for composite score keys (same as progress table).
 */
export type ParaPhaseSectionForMilestones = {
	meta: { phaseId: string; phaseNumber?: number; sortOrder?: number };
	/** Report phase index for `phaseSortOrder * 100 + challengeId` score keys. */
	phaseNumber: number;
	studentData: any[];
	challengeIndexById: Map<number, number>;
	challengeData?: { challenges?: Array<{ id?: number }> };
};

function findStudentRowInPhaseSection(
	section: ParaPhaseSectionForMilestones | null | undefined,
	studentParticipantId: unknown
): any | null {
	if (section == null || studentParticipantId == null || studentParticipantId === '') {
		return null;
	}
	const want = String(studentParticipantId);
	const list = Array.isArray(section.studentData) ? section.studentData : [];
	for (const r of list) {
		const rid = r?.id ?? r?.student?.id;
		if (rid != null && String(rid) === want) {
			return r;
		}
	}
	return null;
}

/** Matches {@link ParaProgressTableComponent} `coercePhaseIndex` for score composite keys. */
function coercePhaseIndexForScores(raw: unknown): number {
	const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw);
	if (Number.isFinite(n) && n >= 0) {
		return Math.floor(n);
	}
	return 0;
}

/**
 * Same key resolution as `getCellScoreRecord` (composite `pn * 100 + cid` when pn &gt; 1).
 */
function pickScoreRecord(scores: any, phaseIndex: number, challengeId: number): any {
	if (!scores || typeof scores !== 'object') {
		return undefined;
	}
	const cid = Number(challengeId);
	if (!Number.isFinite(cid)) {
		return undefined;
	}
	const composite = phaseIndex * 100 + cid;
	const pick = (k: number | string) => {
		const v = scores[k as any];
		return v !== undefined && v !== null ? v : undefined;
	};
	return pick(composite) ?? pick(String(composite)) ?? (phaseIndex <= 1 ? pick(cid) ?? pick(String(cid)) : undefined);
}

function phaseIndexForSection(section: ParaPhaseSectionForMilestones): number {
	return coercePhaseIndexForScores(section.phaseNumber ?? section.meta?.phaseNumber ?? section.meta?.sortOrder);
}

/** Score cell for the 1-based challenge column index (same order as progress table headers). */
export function scoreRecordAtChallengeIndex1Based(
	row: any,
	section: ParaPhaseSectionForMilestones | null | undefined,
	index1Based: number
): any {
	if (!section?.challengeData?.challenges?.length || !Number.isFinite(index1Based) || index1Based < 1) {
		return undefined;
	}
	const challenges = section.challengeData.challenges;
	if (index1Based > challenges.length) {
		return undefined;
	}
	const cid = Number(challenges[index1Based - 1]?.id);
	if (!Number.isFinite(cid)) {
		return undefined;
	}
	return pickScoreRecord(row?.scores, phaseIndexForSection(section), cid);
}

/**
 * Aligns with progress table “mastered” / pass semantics (`isPass` or score ≥ 3).
 */
export function isPassingScoreRecord(rec: any): boolean {
	if (rec == null || typeof rec !== 'object') {
		return false;
	}
	if (rec.isPass === true || rec.isPass === 1) {
		return true;
	}
	const score = rec.score;
	return score != null && Number.isFinite(Number(score)) && Number(score) >= 3;
}

function hasRecordedAttempt(rec: any): boolean {
	if (rec == null || typeof rec !== 'object') {
		return false;
	}
	if (rec.score != null && String(rec.score).trim() !== '') {
		return true;
	}
	return rec.isPass === true || rec.isPass === 1 || rec.isPass === false || rec.isPass === 0;
}

/**
 * - **Achieved**: passing score on **every** challenge in `[startChallengeNumber, endChallengeNumber]`.
 * - **In progress**: not achieved, but at least one scored attempt in that range.
 * - **Not started**: otherwise.
 */
export function milestoneStatusFromPhaseScores(
	row: any,
	section: ParaPhaseSectionForMilestones | null | undefined,
	ms: ParaMilestoneDefinition
): ParaMilestoneStatus {
	if (!section?.challengeData?.challenges?.length) {
		return 'not_started';
	}
	const challengeCount = section.challengeData.challenges.length;
	const start = Math.floor(Number(ms?.startChallengeNumber) || 0);
	const endRaw = Math.floor(Number(ms?.endChallengeNumber) || 0);
	if (endRaw < 1 || start < 1 || endRaw < start || start > challengeCount) {
		return 'not_started';
	}
	const end = Math.min(endRaw, challengeCount);

	let allPass = true;
	let anyAttempt = false;
	for (let i = start; i <= end; i++) {
		const rec = scoreRecordAtChallengeIndex1Based(row, section, i);
		if (hasRecordedAttempt(rec)) {
			anyAttempt = true;
		}
		if (!isPassingScoreRecord(rec)) {
			allPass = false;
		}
	}
	if (allPass) {
		return 'achieved';
	}
	return anyAttempt ? 'in_progress' : 'not_started';
}

function highestIndexFromPhaseScores(
	row: any,
	section: ParaPhaseSectionForMilestones & { challengeData?: { challenges?: Array<{ id?: number }> } }
): number | null {
	const challenges = section.challengeData?.challenges;
	if (!Array.isArray(challenges) || challenges.length === 0) {
		return null;
	}
	const phaseIndex = phaseIndexForSection(section);
	const scores = row?.scores;
	let best: number | null = null;
	for (let i = 0; i < challenges.length; i++) {
		const cid = Number(challenges[i]?.id);
		if (!Number.isFinite(cid)) {
			continue;
		}
		const rec = pickScoreRecord(scores, phaseIndex, cid);
		const has = rec != null && typeof rec === 'object' && (rec.score != null || rec.isPass === true || rec.isPass === 1);
		if (has) {
			best = i + 1;
		}
	}
	return best;
}

/**
 * Prefer `highestChallenge` only when its phase matches this section (avoids false positives from
 * the global row pointer). Otherwise derive progress from this phase's `scores` only.
 */
export function resolveHighestCompletedChallengeIndexForSection(
	row: any,
	section: ParaPhaseSectionForMilestones | null | undefined
): number | null {
	if (!row || !section) {
		return null;
	}
	const challenges = section.challengeData?.challenges;
	if (!Array.isArray(challenges) || challenges.length === 0) {
		return null;
	}

	const hcPhase = row?.highestChallenge?.phase?.phaseId;
	const secPhase = section.meta?.phaseId;
	const phaseIdsAlign = hcPhase == null || secPhase == null || String(hcPhase) === String(secPhase);

	if (phaseIdsAlign) {
		const fromHighest = highestCompletedChallengeIndex1Based(row, section);
		if (fromHighest != null) {
			return fromHighest;
		}
	}
	return highestIndexFromPhaseScores(row, section);
}

/**
 * Every milestone in the assessment, in phase section order, with status from that student's row
 * in each phase. Phases with **no challenge grid** are skipped (no empty-phase milestone strip).
 */
export function listMilestonesWithStatusForAssessment(
	milestones: readonly ParaMilestoneDefinition[] | null | undefined,
	phaseSections: readonly ParaPhaseSectionForMilestones[] | null | undefined,
	studentParticipantId: unknown
): ParaMilestoneWithStatus[] {
	if (studentParticipantId == null || studentParticipantId === '') {
		return [];
	}
	const out: ParaMilestoneWithStatus[] = [];
	for (const section of phaseSections ?? []) {
		const ch = section.challengeData?.challenges;
		if (!Array.isArray(ch) || ch.length < 1) {
			continue;
		}
		const row = findStudentRowInPhaseSection(section, studentParticipantId);
		out.push(...listMilestonesWithStatusForPhase(milestones, section.meta?.phaseId, row, section));
	}
	return out;
}

export type ParaMilestoneStatus = 'achieved' | 'in_progress' | 'not_started';

export interface ParaMilestoneWithStatus {
	milestone: ParaMilestoneDefinition;
	status: ParaMilestoneStatus;
}

/** Compact payload for `app-para-student-badge` milestone glyph overlay. */
export type ParaStudentBadgeMilestoneOverlay = {
	readonly tier: number;
	readonly accentHex: string | null;
	/**
	 * True when the tier comes from “next” milestones only (nothing achieved or in progress yet).
	 * UI may render slightly softer behind the challenge pill.
	 */
	readonly isAspirational?: boolean;
};

/**
 * Milestone badge art for the PARA student tile: uses the highest **achieved** band (by max
 * `sortOrder`). Attempted-but-not-passed bands (`in_progress`) do not earn a tier — the grid shows
 * × for those. When nothing is achieved yet, falls back to the lowest-`sortOrder` unachieved band
 * (`not_started` or `in_progress`) as an aspirational anchor.
 */
export function resolveStudentBadgeMilestoneOverlay(
	items: readonly ParaMilestoneWithStatus[] | null | undefined
): ParaStudentBadgeMilestoneOverlay | null {
	if (!items?.length) {
		return null;
	}
	let bestAch: ParaMilestoneWithStatus | null = null;
	let bestNext: ParaMilestoneWithStatus | null = null;

	for (const x of items) {
		const so = Number(x.milestone?.sortOrder ?? 0);
		if (!Number.isFinite(so)) {
			continue;
		}
		if (x.status === 'achieved') {
			if (!bestAch || so > Number(bestAch.milestone?.sortOrder ?? 0)) {
				bestAch = x;
			}
		} else if (x.status === 'not_started' || x.status === 'in_progress') {
			if (!bestNext || so < Number(bestNext.milestone?.sortOrder ?? Infinity)) {
				bestNext = x;
			}
		}
	}

	const wrap = (row: ParaMilestoneWithStatus, isAspirational: boolean): ParaStudentBadgeMilestoneOverlay => ({
		tier: clampParaMilestoneIconTier(row.milestone.sortOrder, 1),
		accentHex: normalizeChipColor(row.milestone.color ?? null),
		isAspirational,
	});

	if (bestAch) {
		return wrap(bestAch, false);
	}
	if (bestNext) {
		return wrap(bestNext, true);
	}
	return null;
}

export interface ParaMilestoneStatusCountRow {
	milestone: ParaMilestoneDefinition;
	milestoneId: string;
	label: string;
	color: string | null;
	sortOrder: number;
	achieved: number;
	inProgress: number;
	notStarted: number;
	total: number;
}

function participantIdForRow(row: any): unknown {
	return row?.id ?? row?.student?.id;
}

function milestoneKey(ms: ParaMilestoneDefinition): string {
	return `${ms?.phaseId ?? ''}:${ms?.milestoneId ?? ''}`;
}

function sortMilestones(a: ParaMilestoneDefinition, b: ParaMilestoneDefinition): number {
	const ao = Number(a?.sortOrder ?? 0);
	const bo = Number(b?.sortOrder ?? 0);
	if (ao !== bo) {
		return ao - bo;
	}
	return String(a?.label ?? a?.milestoneId ?? '').localeCompare(String(b?.label ?? b?.milestoneId ?? ''));
}

export function rollupMilestoneStatusCounts(
	milestones: readonly ParaMilestoneDefinition[] | null | undefined,
	phaseSections: readonly ParaPhaseSectionForMilestones[] | null | undefined,
	students: readonly any[] | null | undefined
): ParaMilestoneStatusCountRow[] {
	const availablePhaseIds = new Set<string>();
	for (const section of phaseSections ?? []) {
		const ch = section?.challengeData?.challenges;
		const phaseId = section?.meta?.phaseId;
		if (phaseId != null && Array.isArray(ch) && ch.length > 0) {
			availablePhaseIds.add(String(phaseId));
		}
	}

	const visibleMilestones = [...(milestones ?? [])]
		.filter((m) => m?.phaseId != null && availablePhaseIds.has(String(m.phaseId)))
		.sort(sortMilestones);
	const byKey = new Map<string, ParaMilestoneStatusCountRow>();
	for (const milestone of visibleMilestones) {
		byKey.set(milestoneKey(milestone), {
			milestone,
			milestoneId: String(milestone.milestoneId ?? ''),
			label: String(milestone.label ?? milestone.milestoneId ?? ''),
			color: milestone.color ?? null,
			sortOrder: Number(milestone.sortOrder ?? 0),
			achieved: 0,
			inProgress: 0,
			notStarted: 0,
			total: 0,
		});
	}

	for (const student of students ?? []) {
		const participantId = participantIdForRow(student);
		if (participantId == null || participantId === '') {
			continue;
		}
		for (const item of listMilestonesWithStatusForAssessment(milestones, phaseSections, participantId)) {
			const row = byKey.get(milestoneKey(item.milestone));
			if (!row) {
				continue;
			}
			row.total++;
			if (item.status === 'achieved') {
				row.achieved++;
			} else if (item.status === 'in_progress') {
				row.inProgress++;
			} else {
				row.notStarted++;
			}
		}
	}

	return [...byKey.values()];
}

/**
 * 1-based challenge column index for the student's highest completed challenge within `section`,
 * or `null` when there is no scored challenge for that phase.
 */
export function highestCompletedChallengeIndex1Based(
	row: { highestChallenge?: { challengeId?: number; id?: number } | null } | null | undefined,
	section: ParaChallengeIndexSection | null | undefined
): number | null {
	if (!row || !section) {
		return null;
	}
	const rawId = row?.highestChallenge?.challengeId ?? row?.highestChallenge?.id;
	const challengeId = Number(rawId);
	if (!Number.isFinite(challengeId)) {
		return null;
	}
	const idx = section.challengeIndexById.get(challengeId);
	return idx != null ? idx : null;
}

/**
 * All milestones for `phaseId`, ordered for display. Status uses **passing scores on every
 * challenge** in each band (`startChallengeNumber` … `endChallengeNumber`).
 */
export function listMilestonesWithStatusForPhase(
	milestones: readonly ParaMilestoneDefinition[] | null | undefined,
	phaseId: string | null | undefined,
	row: any,
	section: ParaPhaseSectionForMilestones | null | undefined
): ParaMilestoneWithStatus[] {
	const pid = phaseId != null ? String(phaseId).trim() : '';
	if (!pid) {
		return [];
	}
	const raw = (milestones ?? []).filter((m) => String(m?.phaseId) === pid);
	const sorted = [...raw].sort((a, b) => {
		const ao = Number(a?.sortOrder ?? 0);
		const bo = Number(b?.sortOrder ?? 0);
		if (ao !== bo) {
			return ao - bo;
		}
		return Number(a?.startChallengeNumber ?? 0) - Number(b?.startChallengeNumber ?? 0);
	});
	return sorted.map((m) => ({
		milestone: m,
		status: milestoneStatusFromPhaseScores(row, section, m),
	}));
}

/**
 * Milestones in `phaseId` where the student has a **passing** score on **each** challenge in the band.
 */
export function listAchievedMilestonesForPhase(
	milestones: readonly ParaMilestoneDefinition[] | null | undefined,
	phaseId: string | null | undefined,
	row: any,
	section: ParaPhaseSectionForMilestones | null | undefined
): ParaMilestoneDefinition[] {
	return listMilestonesWithStatusForPhase(milestones, phaseId, row, section)
		.filter((x) => x.status === 'achieved')
		.map((x) => x.milestone);
}
